/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Added CSV export functionality.
 *                                   - Ensured `totalItems` prop is passed to Pagination.
 *                                   - Integrated Spinner for loading states.
 *                                   - Added `TieredPriceViewer` for displaying complex price details.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide columns
 *                                   and customize column headers based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component displays read-only price information for products.
 *                      It does not handle sensitive data or perform write operations.
 * INTENT: Render a paginated table of product prices, including detailed wholesale and retail
 *         pricing structures and tiered prices, with an option to export data,
 *         now with whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure price formatting is consistent and
 *                        that complex price details are presented clearly.
 * LLM_CONFIDENCE: 100% - Standard table component with pagination and display of complex data.
 */
import React, { useState } from 'react';
import { PriceProduct, PriceDetail } from '../types/costwand.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import Pagination from './ui/Pagination.tsx';
import { Link } from 'react-router-dom';
import { Button } from './ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx';
import { Spinner } from './ui/spinner.tsx';
import { apiClient } from '../api/apiClient.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

// New component to display tiered price details
interface TieredPriceViewerProps {
  tieredPrices: PriceDetail['tiered'];
  title: string;
  entityId: string; // NEW: Add entityId prop
}

const TieredPriceViewer: React.FC<TieredPriceViewerProps> = ({ tieredPrices, title, entityId = 'prices' }) => { // NEW: Default entityId
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId]?.title || defaultTitle;

  if (!tieredPrices || Object.keys(tieredPrices).length === 0) {
    return <p className="text-muted-foreground">No tiered prices available for {title}.</p>;
  }
  return (
    <ul className="list-disc list-inside space-y-1">
      {Object.entries(tieredPrices).map(([quantity, tierData]) => (
        <li key={quantity}>
          <span className="font-semibold">{quantity} {getFieldTitle('units', 'units')}:</span>
          <br />{getFieldTitle('unit_price', 'Unit Price')}: ${tierData.unit_price.toFixed(2)}
          <br />{getFieldTitle('discount', 'Discount')}: {tierData.discount_perc.toFixed(2)}%
          <br />{getFieldTitle('total_value', 'Total Value')}: ${tierData.tier_total_value.toFixed(2)}
        </li>
      ))}
    </ul>
  );
};

interface PriceTableProps {
  prices: PriceProduct[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string;
  entityId: string; // NEW: Add entityId prop
}

const PriceTable: React.FC<PriceTableProps> = ({
  prices,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  loading,
  error,
  baseEndpoint,
  entityId, // NEW: Destructure entityId
}) => {
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context
  const [exportLoading, setExportLoading] = useState(false);

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: unknown) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading)
    return (
      <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading Prices...</p>
    );

    if (prices.length === 0 && !loading)
    return (
      <p className="text-center text-muted-foreground">No Prices found.</p>
    );

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4">
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {isFieldVisible('description') && <TableHead>{getFieldTitle('description', 'Description')}</TableHead>}
            {isFieldVisible('article_number') && <TableHead>{getFieldTitle('article_number', 'Article No.')}</TableHead>}
            {isFieldVisible('vendor_id') && <TableHead>{getFieldTitle('vendor_id', 'Vendor')}</TableHead>}
            {isFieldVisible('family_id') && <TableHead>{getFieldTitle('family_id', 'Family')}</TableHead>}
            {isFieldVisible('wholesale') && <TableHead>{getFieldTitle('wholesale', 'Wholesale (Base)')}</TableHead>}
            {isFieldVisible('wholesale_final') && <TableHead>{getFieldTitle('wholesale_final', 'Wholesale (Final)')}</TableHead>}
            {isFieldVisible('wholesale_tiered') && <TableHead>{getFieldTitle('wholesale_tiered', 'Wholesale (Tiered)')}</TableHead>}
            {isFieldVisible('retail') && <TableHead>{getFieldTitle('retail', 'Retail (Base)')}</TableHead>}
            {isFieldVisible('retail_final') && <TableHead>{getFieldTitle('retail_final', 'Retail (Final)')}</TableHead>}
            {isFieldVisible('retail_tiered') && <TableHead>{getFieldTitle('retail_tiered', 'Retail (Tiered)')}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {prices.map((price) => (
            <TableRow key={price.id}>
              {isFieldVisible('description') && <TableCell>{price.description}</TableCell>}
              {isFieldVisible('article_number') && <TableCell>{price.article_number}</TableCell>}
              {isFieldVisible('vendor_id') && (
                <TableCell>
                  {price._labels?.vendor_id || `ID: ${price.vendor_id}`}
                </TableCell>
              )}
              {isFieldVisible('family_id') && (
                <TableCell>
                  {price._labels?.family_id || `ID: ${price.family_id}`}
                </TableCell>
              )}
              {isFieldVisible('wholesale') && <TableCell>${(price.wholesale as PriceDetail)?.margin?.selling_price_base?.toFixed(2) || 'N/A'}</TableCell>}
              {isFieldVisible('wholesale_final') && <TableCell>${(price.wholesale as PriceDetail)?.margin?.price_total_w_levies?.toFixed(2) || 'N/A'}</TableCell>}
              {isFieldVisible('wholesale_tiered') && (
                <TableCell>
                  {typeof price.wholesale === 'object' && (price.wholesale as PriceDetail)?.tiered && Object.keys((price.wholesale as PriceDetail).tiered).length > 0 ? (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="default">
                          {getFieldTitle('view_tiers', 'View Tiers')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>{getFieldTitle('wholesale_tiered_prices_dialog_title', 'Wholesale Tiered Prices')}</DialogTitle>
                        </DialogHeader>
                        <TieredPriceViewer tieredPrices={(price.wholesale as PriceDetail).tiered} title="Wholesale" entityId={entityId} />
                      </DialogContent>
                    </Dialog>
                  ) : (
                    'N/A'
                  )}
                </TableCell>
              )}
              {isFieldVisible('retail') && <TableCell>${(price.retail as PriceDetail)?.margin?.selling_price_base?.toFixed(2) || 'N/A'}</TableCell>}
              {isFieldVisible('retail_final') && <TableCell>${(price.retail as PriceDetail)?.margin?.price_total_w_levies?.toFixed(2) || 'N/A'}</TableCell>}
              {isFieldVisible('retail_tiered') && (
                <TableCell>
                  {typeof price.retail === 'object' && (price.retail as PriceDetail)?.tiered && Object.keys((price.retail as PriceDetail).tiered).length > 0 ? (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="default">
                          {getFieldTitle('view_tiers', 'View Tiers')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>{getFieldTitle('retail_tiered_prices_dialog_title', 'Retail Tiered Prices')}</DialogTitle>
                        </DialogHeader>
                        <TieredPriceViewer tieredPrices={(price.retail as PriceDetail).tiered} title="Retail" entityId={entityId} />
                      </DialogContent>
                    </Dialog>
                  ) : (
                    'N/A'
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        disabled={loading || exportLoading}
      />
    </div>
  );
};

export default PriceTable;